// src/components/admin/Stats.js
import React from "react";
import {
  useGetSubmissionStatsQuery,
  useGetOnlineGeneralAppsQuery,
  useGetApplicationCountQuery,
  useGetDasApplicationCountQuery,
  useGetSessionListQuery,
  useGetSessionSummaryQuery,
  useGetBlockingSessionsQuery,

  // ✅ NEW monitors (add these endpoints in ../../services/stats)
  useGetActiveRequestsQuery,
  useGetOpenTransactionsQuery,
  useKillSessionMutation,
  useKillRequestMutation,
} from "../../services/stats";

import { useGetAdminStatusQuery } from "../../services/admin/admin";

import "../../assets/scss/components/stats.scss";
import { formatDistanceToNow } from "date-fns";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import AppsIcon from "@mui/icons-material/Apps";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import RefreshIcon from "@mui/icons-material/Refresh";
import DangerousIcon from "@mui/icons-material/Dangerous";

const truncate = (s, n = 140) => {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}...` : s;
};

const safeNum = (v, fallback = 0) =>
  Number.isFinite(Number(v)) ? Number(v) : fallback;

const Stats = () => {
  const { data: adminStatus } = useGetAdminStatusQuery();
  const isSchoolAdminType =
    adminStatus?.admin_type_id === 3 || adminStatus?.admin_type_id === 7;
  const [tab, setTab] = React.useState(0);

  // “Kill” should only exist for high-privilege users (you can tighten this)
  const canKill =
    adminStatus?.admin_type_id === 1 || adminStatus?.admin_type_id === 2;

  const {
    data: submissionStats,
    error: submissionStatsError,
    isLoading: isSubmissionStatsLoading,
    refetch: refetchSubmissionStats,
  } = useGetSubmissionStatsQuery();

  const {
    data: blockingSessions,
    error: blockingSessionsError,
    isLoading: isBlockingSessionsLoading,
    refetch: refetchBlocking,
  } = useGetBlockingSessionsQuery();

  const {
    data: onlineGeneralApps,
    error: onlineGeneralAppsError,
    isLoading: isOnlineGeneralAppsLoading,
    refetch: refetchOnline,
  } = useGetOnlineGeneralAppsQuery();

  const {
    data: applicationCount,
    error: applicationCountError,
    isLoading: isApplicationCountLoading,
    refetch: refetchAppCount,
  } = useGetApplicationCountQuery();

  const {
    data: dasApplicationCount,
    error: dasApplicationCountError,
    isLoading: isDasApplicationCountLoading,
    refetch: refetchDasCount,
  } = useGetDasApplicationCountQuery();

  const {
    data: sessionList,
    error: sessionListError,
    isLoading: isSessionListLoading,
    refetch: refetchSessionList,
  } = useGetSessionListQuery();

  const {
    data: sessionSummary,
    error: sessionSummaryError,
    isLoading: isSessionSummaryLoading,
    refetch: refetchSessionSummary,
  } = useGetSessionSummaryQuery();

  // ✅ NEW: Active requests (running/blocked) + open transactions
  const {
    data: activeRequests,
    error: activeRequestsError,
    isLoading: isActiveRequestsLoading,
    refetch: refetchActiveRequests,
  } = useGetActiveRequestsQuery(undefined, { pollingInterval: 10000 });

  const {
    data: openTransactions,
    error: openTransactionsError,
    isLoading: isOpenTransactionsLoading,
    refetch: refetchOpenTransactions,
  } = useGetOpenTransactionsQuery(undefined, { pollingInterval: 10000 });

  const [killSession, { isLoading: isKillingSession }] =
    useKillSessionMutation();
  const [killRequest, { isLoading: isKillingRequest }] =
    useKillRequestMutation();

  // Current time
  const currentTime = new Date();

  // Calculate active sessions
  const activeSessions = React.useMemo(() => {
    if (
      onlineGeneralApps?.connections &&
      Array.isArray(onlineGeneralApps.connections)
    ) {
      return onlineGeneralApps.connections.length;
    }
    return 0;
  }, [onlineGeneralApps]);

  // Total applications
  const totalApplications =
    submissionStats?.summary && Array.isArray(submissionStats.summary)
      ? submissionStats.summary.reduce(
          (acc, stat) => acc + safeNum(stat.total_records),
          0
        )
      : 0;

  const anyLoading =
    isSubmissionStatsLoading ||
    isBlockingSessionsLoading ||
    isOnlineGeneralAppsLoading ||
    isApplicationCountLoading ||
    isDasApplicationCountLoading ||
    isSessionListLoading ||
    isSessionSummaryLoading ||
    isActiveRequestsLoading ||
    isOpenTransactionsLoading;

  const anyError =
    submissionStatsError ||
    blockingSessionsError ||
    onlineGeneralAppsError ||
    applicationCountError ||
    dasApplicationCountError ||
    sessionListError ||
    sessionSummaryError ||
    activeRequestsError ||
    openTransactionsError;

  const refreshAll = () => {
    refetchSubmissionStats();
    refetchBlocking();
    refetchOnline();
    refetchAppCount();
    refetchDasCount();
    refetchSessionList();
    refetchSessionSummary();
    refetchActiveRequests();
    refetchOpenTransactions();
  };

  const onKillSession = async (sessionId) => {
    if (!canKill) return;
    const ok = window.confirm(
      `Kill SQL session ${sessionId}? This can break in-flight work.`
    );
    if (!ok) return;
    try {
      await killSession({ sessionId }).unwrap();
      refreshAll();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to kill session. Check permissions/logs.");
    }
  };

  const onKillRequest = async (sessionId, requestId) => {
    if (!canKill) return;
    const ok = window.confirm(
      `Kill request ${requestId} (session ${sessionId})? This will cancel the running statement.`
    );
    if (!ok) return;
    try {
      await killRequest({ sessionId, requestId }).unwrap();
      refreshAll();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to kill request. Check permissions/logs.");
    }
  };

  if (anyLoading) return <div className="loading">Loading...</div>;
  if (anyError) return <div className="error">Error fetching data</div>;

  const blockingCount = Array.isArray(blockingSessions)
    ? blockingSessions.length
    : 0;
  const activeReqCount =
    activeRequests?.requests && Array.isArray(activeRequests.requests)
      ? activeRequests.requests.length
      : Array.isArray(activeRequests)
      ? activeRequests.length
      : 0;

  const openTxnCount =
    openTransactions?.transactions &&
    Array.isArray(openTransactions.transactions)
      ? openTransactions.transactions.length
      : Array.isArray(openTransactions)
      ? openTransactions.length
      : 0;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: "100vw" }}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Box />
          <Typography variant="h4" fontWeight={800} color="primary.main">
            NLSF Awards System Status
          </Typography>
          <IconButton onClick={refreshAll} title="Refresh all">
            <RefreshIcon />
          </IconButton>
        </Stack>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          centered
          sx={{ mb: 2 }}
        >
          <Tab label="Application" />
          <Tab label="Database" />
        </Tabs>
      </Paper>
      {tab === 0 && (
        <>
          {/* Application Tab Navigation */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button href="#submission-stats" variant="outlined" size="small">
              Submission Stats
            </Button>
            <Button href="#recent-activity" variant="outlined" size="small">
              Recent Activity
            </Button>
            <Button href="#general-app-count" variant="outlined" size="small">
              General App Count
            </Button>
            <Button href="#das-app-count" variant="outlined" size="small">
              DAS App Count
            </Button>
          </Stack>
          {/* Application Tab Content */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 2 }}
              >
                <Typography
                  id="submission-stats"
                  variant="h5"
                  fontWeight={800}
                  sx={{ mb: 1 }}
                >
                  Submission Stats - Applications
                </Typography>
                <Box
                  className="table-responsive"
                  sx={{ background: "#f7fafc", borderRadius: 2, p: 1 }}
                >
                  <table style={{ width: "100%", fontSize: "0.95rem" }}>
                    <thead style={{ background: "#e3f2fd" }}>
                      <tr>
                        <th rowSpan="2">
                          <Tooltip title="School">
                            <SchoolIcon color="primary" />
                          </Tooltip>
                        </th>
                        <th rowSpan="2">
                          <Tooltip title="Total Applications">
                            <AppsIcon color="secondary" />
                          </Tooltip>
                        </th>
                        <th colSpan="3" style={{ background: "#fffde7" }}>
                          <Tooltip title="DAS Awards">
                            <GroupIcon sx={{ color: "#fbc02d" }} />
                          </Tooltip>
                        </th>
                        <th colSpan="3" style={{ background: "#e8f5e9" }}>
                          <Tooltip title="General Awards">
                            <AppsIcon sx={{ color: "#43a047" }} />
                          </Tooltip>
                        </th>
                      </tr>
                      <tr>
                        <th>
                          <Tooltip title="Applications">
                            <AppsIcon
                              fontSize="small"
                              sx={{ color: "#fbc02d" }}
                            />
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip title="Submitted">
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{ color: "#43a047" }}
                            />
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip title="Not Submitted">
                            <CancelIcon
                              fontSize="small"
                              sx={{ color: "#e53935" }}
                            />
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip title="Applications">
                            <AppsIcon
                              fontSize="small"
                              sx={{ color: "#43a047" }}
                            />
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip title="Submitted">
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{ color: "#43a047" }}
                            />
                          </Tooltip>
                        </th>
                        <th>
                          <Tooltip title="Not Submitted">
                            <CancelIcon
                              fontSize="small"
                              sx={{ color: "#e53935" }}
                            />
                          </Tooltip>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissionStats?.summary &&
                      Array.isArray(submissionStats.summary) ? (
                        submissionStats.summary.map((stat, index) => (
                          <tr
                            key={index}
                            style={{
                              background: index % 2 === 0 ? "#f9fbe7" : "#fff",
                            }}
                          >
                            <td>{stat.school}</td>
                            <td>{stat.total_records}</td>
                            <td className="das-applications">
                              {stat.das_applications}
                            </td>
                            <td className="das-submitted-applications">
                              {stat.das_submitted_applications}
                            </td>
                            <td className="das-null-applications">
                              {stat.das_null_applications}
                            </td>
                            <td className="general-applications">
                              {stat.general_applications}
                            </td>
                            <td className="general-submitted-applications">
                              {stat.general_submitted_applications}
                            </td>
                            <td className="general-null-applications">
                              {stat.general_null_applications}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8">No submission stats available.</td>
                        </tr>
                      )}
                      {submissionStats?.summary &&
                        Array.isArray(submissionStats.summary) && (
                          <tr
                            className="total-row"
                            style={{
                              fontWeight: "bold",
                              background: "#ffe082",
                            }}
                          >
                            <td>Total</td>
                            <td>{totalApplications}</td>
                            <td className="das-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc + safeNum(stat.das_applications),
                                0
                              )}
                            </td>
                            <td className="das-submitted-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc +
                                  safeNum(stat.das_submitted_applications),
                                0
                              )}
                            </td>
                            <td className="das-null-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc + safeNum(stat.das_null_applications),
                                0
                              )}
                            </td>
                            <td className="general-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc + safeNum(stat.general_applications),
                                0
                              )}
                            </td>
                            <td className="general-submitted-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc +
                                  safeNum(stat.general_submitted_applications),
                                0
                              )}
                            </td>
                            <td className="general-null-applications-total">
                              {submissionStats.summary.reduce(
                                (acc, stat) =>
                                  acc + safeNum(stat.general_null_applications),
                                0
                              )}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </Box>
              </Paper>
              {!isSchoolAdminType && (
                <Paper
                  elevation={1}
                  sx={{
                    p: { xs: 1, sm: 2 },
                    mb: 2,
                    borderRadius: 2,
                    background: "#e3f2fd",
                  }}
                >
                  <Typography
                    id="recent-activity"
                    variant="h5"
                    fontWeight={800}
                    sx={{ mb: 1, color: "#1976d2" }}
                  >
                    <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} /> Recent
                    Activity ({onlineGeneralApps?.connections?.length || 0})
                  </Typography>
                  <Box className="table-responsive">
                    <table style={{ width: "100%", fontSize: "0.95rem" }}>
                      <thead style={{ background: "#bbdefb" }}>
                        <tr>
                          <th>
                            <Tooltip title="Last Connection">
                              <AppsIcon fontSize="small" color="primary" />
                            </Tooltip>
                          </th>
                          <th>
                            <Tooltip title="Student ID">
                              <GroupIcon fontSize="small" color="secondary" />
                            </Tooltip>
                          </th>
                          <th>
                            <Tooltip title="First Name">
                              <SchoolIcon fontSize="small" color="primary" />
                            </Tooltip>
                          </th>
                          <th>
                            <Tooltip title="Last Name">
                              <SchoolIcon fontSize="small" color="primary" />
                            </Tooltip>
                          </th>
                          <th>
                            <Tooltip title="Email">
                              <AppsIcon fontSize="small" color="secondary" />
                            </Tooltip>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {onlineGeneralApps?.connections &&
                        Array.isArray(onlineGeneralApps.connections) &&
                        onlineGeneralApps.connections.length > 0 ? (
                          onlineGeneralApps.connections.map((app) => (
                            <tr
                              key={app.id}
                              style={{
                                backgroundColor: app.isAdmin
                                  ? "mistyrose"
                                  : "inherit",
                              }}
                            >
                              <td>
                                {formatDistanceToNow(
                                  new Date(app.last_accessed)
                                )}{" "}
                                ago
                              </td>
                              <td>{app.id}</td>
                              <td>{app.first_name}</td>
                              <td>{app.last_name}</td>
                              <td>{app.email}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5">No connections found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 1, sm: 2 },
                  mb: 2,
                  borderRadius: 2,
                  background: "#e8f5e9",
                }}
              >
                <Typography
                  id="general-app-count"
                  variant="h5"
                  fontWeight={800}
                  sx={{ mb: 1, color: "#388e3c" }}
                >
                  General Application Count
                </Typography>
                <table style={{ width: "100%", fontSize: "0.95rem" }}>
                  <thead style={{ background: "#c8e6c9" }}>
                    <tr>
                      <th>
                        <Tooltip title="Graduation Year">
                          <SchoolIcon fontSize="small" color="primary" />
                        </Tooltip>
                      </th>
                      <th>
                        <Tooltip title="Total Applications">
                          <AppsIcon fontSize="small" color="secondary" />
                        </Tooltip>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicationCount?.applicationCount &&
                    Array.isArray(applicationCount.applicationCount) &&
                    applicationCount.applicationCount.length > 0 ? (
                      applicationCount.applicationCount.map((app, index) => (
                        <tr
                          key={index}
                          style={{
                            background: index % 2 === 0 ? "#e8f5e9" : "#fff",
                          }}
                        >
                          <td>{app.grad_year || "N/A"}</td>
                          <td>{app.count}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2">None found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Paper>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 1, sm: 2 },
                  mb: 2,
                  borderRadius: 2,
                  background: "#fffde7",
                }}
              >
                <Typography
                  id="das-app-count"
                  variant="h5"
                  fontWeight={800}
                  sx={{ mb: 1, color: "#fbc02d" }}
                >
                  DAS Application Count
                </Typography>
                <table style={{ width: "100%", fontSize: "0.95rem" }}>
                  <thead style={{ background: "#fffde7" }}>
                    <tr>
                      <th>
                        <Tooltip title="Graduation Year">
                          <SchoolIcon fontSize="small" color="primary" />
                        </Tooltip>
                      </th>
                      <th>
                        <Tooltip title="Total Applications (non-Admin)">
                          <GroupIcon
                            fontSize="small"
                            sx={{ color: "#fbc02d" }}
                          />
                        </Tooltip>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dasApplicationCount?.dasApplicationCount &&
                    Array.isArray(dasApplicationCount.dasApplicationCount) &&
                    dasApplicationCount.dasApplicationCount.length > 0 ? (
                      dasApplicationCount.dasApplicationCount.map(
                        (app, index) => (
                          <tr
                            key={index}
                            style={{
                              background: index % 2 === 0 ? "#fffde7" : "#fff",
                            }}
                          >
                            <td>{app.grad_year || "N/A"}</td>
                            <td>{app.count}</td>
                          </tr>
                        )
                      )
                    ) : (
                      <tr>
                        <td colSpan="2">None found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
      {tab === 1 && (
        <>
          {/* Database Tab Navigation */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button href="#blocking-sessions" variant="outlined" size="small">
              Blocking Sessions
            </Button>
            <Button href="#active-requests" variant="outlined" size="small">
              Active Requests
            </Button>
            <Button href="#open-transactions" variant="outlined" size="small">
              Open Transactions
            </Button>
            <Button href="#session-list" variant="outlined" size="small">
              Session List
            </Button>
            <Button href="#session-summary" variant="outlined" size="small">
              Session Summary
            </Button>
          </Stack>
          {/* Database Tab Content */}
          {/* Session List */}
          <Paper
            elevation={1}
            sx={{
              p: { xs: 1, sm: 2 },
              mb: 2,
              borderRadius: 2,
              background: "#fce4ec",
            }}
          >
            <Typography
              id="session-list"
              variant="h5"
              fontWeight={800}
              sx={{ mb: 1, color: "#d81b60" }}
            >
              Session List
            </Typography>
            <table style={{ width: "100%", fontSize: "0.95rem" }}>
              <thead style={{ background: "#f8bbd0" }}>
                <tr>
                  <th>
                    <Tooltip title="Sessions">
                      <GroupIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Host Name">
                      <AppsIcon fontSize="small" color="primary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Host Process ID">
                      <AppsIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Program Name">
                      <SchoolIcon fontSize="small" color="primary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Database">
                      <AppsIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                  {canKill && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sessionList?.sessionList &&
                Array.isArray(sessionList.sessionList) ? (
                  sessionList.sessionList.map((session, index) => (
                    <tr
                      key={index}
                      style={{
                        background: index % 2 === 0 ? "#fce4ec" : "#fff",
                      }}
                    >
                      <td>{session.sessions}</td>
                      <td>{session.host_name}</td>
                      <td>{session.host_process_id}</td>
                      <td>{session.program_name}</td>
                      <td>{session.database_name}</td>
                      {canKill && (
                        <td>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={isKillingSession}
                            onClick={() => onKillSession(session.sessions)}
                          >
                            Kill
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canKill ? 6 : 5}>No sessions found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Paper>
          {/* Session Summary */}
          <Paper
            elevation={1}
            sx={{
              p: { xs: 1, sm: 2 },
              mb: 2,
              borderRadius: 2,
              background: "#e1f5fe",
            }}
          >
            <Typography
              id="session-summary"
              variant="h5"
              fontWeight={800}
              sx={{ mb: 1, color: "#0288d1" }}
            >
              Session Summary
            </Typography>
            <table style={{ width: "100%", fontSize: "0.95rem" }}>
              <thead style={{ background: "#b3e5fc" }}>
                <tr>
                  <th>
                    <Tooltip title="Minutes Asleep">
                      <CancelIcon fontSize="small" sx={{ color: "#e53935" }} />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Session ID">
                      <GroupIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Host Name">
                      <AppsIcon fontSize="small" color="primary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Host Process ID">
                      <AppsIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Program Name">
                      <SchoolIcon fontSize="small" color="primary" />
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip title="Last SQL">
                      <AppsIcon fontSize="small" color="secondary" />
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sessionSummary?.sessionSummary &&
                Array.isArray(sessionSummary.sessionSummary) &&
                sessionSummary.sessionSummary.length > 0 ? (
                  sessionSummary.sessionSummary.map((summary, index) => (
                    <tr
                      key={summary.session_id}
                      style={{
                        background: index % 2 === 0 ? "#e1f5fe" : "#fff",
                      }}
                    >
                      <td>{summary.minutes_asleep}</td>
                      <td>{summary.session_id}</td>
                      <td>{summary.host_name}</td>
                      <td>{summary.host_process_id}</td>
                      <td>{summary.program_name}</td>
                      <td>{truncate(summary.last_sql, 140)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">None found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Paper>
          {/* Database Tab Stats Chips */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mb: 2, flexWrap: "wrap" }}
          >
            <Chip
              label={`Blocking: ${blockingCount}`}
              color={blockingCount > 0 ? "error" : "success"}
              variant={blockingCount > 0 ? "filled" : "outlined"}
            />
            <Chip
              label={`Active Requests: ${activeReqCount}`}
              color={activeReqCount > 10 ? "warning" : "default"}
              variant="outlined"
            />
            <Chip
              label={`Open Txns: ${openTxnCount}`}
              color={openTxnCount > 0 ? "warning" : "default"}
              variant="outlined"
            />
            <Chip
              label={`Recent Sessions: ${activeSessions}`}
              variant="outlined"
            />
          </Stack>
          {/* Blocking Sessions */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 2,
              borderRadius: 3,
              background: "#ffebee",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                id="blocking-sessions"
                variant="h5"
                fontWeight={800}
                color="error.main"
              >
                Blocking Sessions
              </Typography>
              <Button
                size="small"
                onClick={refetchBlocking}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Stack>
            {blockingCount > 0 ? (
              <>
                <Typography color="error" variant="body2" sx={{ mt: 1, mb: 1 }}>
                  {blockingCount} blocking session(s) detected
                </Typography>
                <Box className="table-responsive">
                  <table style={{ width: "100%", fontSize: "0.95rem" }}>
                    <thead>
                      <tr>
                        <th>Blocked</th>
                        <th>Blocker</th>
                        <th>Wait</th>
                        <th>Wait ms</th>
                        <th>DB</th>
                        <th>Program</th>
                        <th>Blocked SQL</th>
                        {canKill && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {blockingSessions.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            {row.blocked_session_id ??
                              row.blockedSessionId ??
                              row.blocked}
                          </td>
                          <td>
                            {row.blocker_session_id ??
                              row.blockerSessionId ??
                              row.blocker}
                          </td>
                          <td>{row.wait_type ?? row.waitType}</td>
                          <td>{row.wait_time ?? row.waitTime}</td>
                          <td>{row.db ?? row.database_name ?? row.database}</td>
                          <td>{row.program_name ?? row.program}</td>
                          <td style={{ maxWidth: 520 }}>
                            {truncate(
                              row.blocked_statement ?? row.blockedSql,
                              160
                            )}
                          </td>
                          {canKill && (
                            <td>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  disabled={isKillingSession}
                                  startIcon={<DangerousIcon />}
                                  onClick={() =>
                                    onKillSession(
                                      row.blocker_session_id ??
                                        row.blockerSessionId ??
                                        row.blocker
                                    )
                                  }
                                >
                                  Kill blocker
                                </Button>
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  disabled={isKillingRequest}
                                  onClick={() =>
                                    onKillRequest(
                                      row.blocked_session_id ??
                                        row.blockedSessionId ??
                                        row.blocked,
                                      row.blocked_request_id ??
                                        row.blockedRequestId ??
                                        row.request_id ??
                                        row.requestId
                                    )
                                  }
                                >
                                  Kill blocked req
                                </Button>
                              </Stack>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </>
            ) : (
              <Typography color="success.main" sx={{ mt: 1 }}>
                No blocking sessions detected
              </Typography>
            )}
          </Paper>
          {/* Active Requests + Open Transactions */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 2 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    id="active-requests"
                    variant="h5"
                    fontWeight={800}
                  >
                    Active Requests (Running / Waiting)
                  </Typography>
                  <Button
                    size="small"
                    onClick={refetchActiveRequests}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh
                  </Button>
                </Stack>
                <Box className="table-responsive">
                  <table style={{ width: "100%", fontSize: "0.95rem" }}>
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th>Req</th>
                        <th>Status</th>
                        <th>Wait</th>
                        <th>Elapsed ms</th>
                        <th>SQL</th>
                        {canKill && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {activeRequests?.requests &&
                      Array.isArray(activeRequests.requests) &&
                      activeRequests.requests.length > 0 ? (
                        activeRequests.requests.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.session_id ?? r.sessionId}</td>
                            <td>{r.request_id ?? r.requestId}</td>
                            <td>{r.status}</td>
                            <td>{r.wait_type ?? r.waitType}</td>
                            <td>{r.total_elapsed_time ?? r.elapsedMs}</td>
                            <td style={{ maxWidth: 420 }}>
                              {truncate(
                                r.sql_text ?? r.sql ?? r.statement,
                                160
                              )}
                            </td>
                            {canKill && (
                              <td>
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  disabled={isKillingRequest}
                                  onClick={() =>
                                    onKillRequest(
                                      r.session_id ?? r.sessionId,
                                      r.request_id ?? r.requestId
                                    )
                                  }
                                >
                                  Kill req
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : Array.isArray(activeRequests) &&
                        activeRequests.length > 0 ? (
                        activeRequests.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.session_id ?? r.sessionId}</td>
                            <td>{r.request_id ?? r.requestId}</td>
                            <td>{r.status}</td>
                            <td>{r.wait_type ?? r.waitType}</td>
                            <td>{r.total_elapsed_time ?? r.elapsedMs}</td>
                            <td style={{ maxWidth: 420 }}>
                              {truncate(
                                r.sql_text ?? r.sql ?? r.statement,
                                160
                              )}
                            </td>
                            {canKill && (
                              <td>
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  disabled={isKillingRequest}
                                  onClick={() =>
                                    onKillRequest(
                                      r.session_id ?? r.sessionId,
                                      r.request_id ?? r.requestId
                                    )
                                  }
                                >
                                  Kill req
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={canKill ? 7 : 6}>No active requests.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: 2 }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    id="open-transactions"
                    variant="h5"
                    fontWeight={800}
                  >
                    Open Transactions
                  </Typography>
                  <Button
                    size="small"
                    onClick={refetchOpenTransactions}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh
                  </Button>
                </Stack>
                <Box className="table-responsive">
                  <table style={{ width: "100%", fontSize: "0.95rem" }}>
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th>Txn</th>
                        <th>Begin</th>
                        <th>State</th>
                        <th>DB</th>
                        <th>Program</th>
                        {canKill && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {openTransactions?.transactions &&
                      Array.isArray(openTransactions.transactions) &&
                      openTransactions.transactions.length > 0 ? (
                        openTransactions.transactions.map((t, idx) => (
                          <tr key={idx}>
                            <td>{t.session_id ?? t.sessionId}</td>
                            <td>{t.transaction_id ?? t.transactionId}</td>
                            <td>
                              {t.transaction_begin_time ??
                                t.begin_time ??
                                t.beginTime}
                            </td>
                            <td>{t.transaction_state ?? t.state}</td>
                            <td>{t.database_name ?? t.db}</td>
                            <td>{t.program_name ?? t.program}</td>
                            {canKill && (
                              <td>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  disabled={isKillingSession}
                                  startIcon={<DangerousIcon />}
                                  onClick={() =>
                                    onKillSession(t.session_id ?? t.sessionId)
                                  }
                                >
                                  Kill session
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : Array.isArray(openTransactions) &&
                        openTransactions.length > 0 ? (
                        openTransactions.map((t, idx) => (
                          <tr key={idx}>
                            <td>{t.session_id ?? t.sessionId}</td>
                            <td>{t.transaction_id ?? t.transactionId}</td>
                            <td>
                              {t.transaction_begin_time ??
                                t.begin_time ??
                                t.beginTime}
                            </td>
                            <td>{t.transaction_state ?? t.state}</td>
                            <td>{t.database_name ?? t.db}</td>
                            <td>{t.program_name ?? t.program}</td>
                            {canKill && (
                              <td>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="contained"
                                  disabled={isKillingSession}
                                  startIcon={<DangerousIcon />}
                                  onClick={() =>
                                    onKillSession(t.session_id ?? t.sessionId)
                                  }
                                >
                                  Kill session
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={canKill ? 7 : 6}>
                            No open transactions.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Stats;
